import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import OnboardingDialog from "arena/components/OnboardingDialog";
import EmailErrorDialog from "arena/components/EmailErrorDialog";
import { useViewer } from "core/components/ViewerProvider";
import { useArena } from "core/components/ArenaProvider";
import AppDrawer from "arena/components/AppDrawer";
import Header from "arena/components/Header";
import Footer from "arena/components/Footer";
import SettingsDialog from "arena/components/SettingsDialog";
import MenuItemDialog from "arena/components/MenuItemDialog";
import GalleryMenuItemDialog from "arena/components/GalleryMenuItemDialog";
import SignInDialog from "core/components/SignInDialog";
import { useApolloClient, useMutation } from "@apollo/client";
import { SignOutMutation } from "arena/mutations";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import analytics from "core/utils/analytics";
import LocalStorage from "core/utils/localstorage";
import SkuflixSettingsIcon from "core/icons/skuflix/SkuflixSettingsIcon";
import ShopIcon from "@mui/icons-material/Shop";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useTranslation } from "react-i18next";
import { isTelegramMiniApp } from "core/utils/telegram";
import TuneIcon from "@mui/icons-material/Tune";
import { usePlayerFullscreen } from "core/components/PlayerFullscreenProvider";
import { useIsMobileApp } from "core/utils/mobileApp";
import useIsAppOnlyPage from "arena/hooks/useIsAppOnlyPage";
import { AppModeProvider } from "./AppModeContext";
import { classes as homepageTitleClasses } from "arena/components/HomepageTitle/HomepageTitle";

const Root = styled("div")({
  minHeight: "100vh",
});

const Content = styled("main", {
  shouldForwardProp: (prop) => !["withoutHeader", "compactTop"].includes(prop),
})(({ theme, withoutHeader, compactTop }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  paddingTop: withoutHeader ? 0 : 54,
  [theme.breakpoints.up("sm")]: {
    paddingTop: withoutHeader ? 0 : 64,
  },

  /**
   * Без шапки отступ над заголовком первой секции выглядит дырой сверху.
   * Уменьшаем его только в первой непустой секции, между секциями
   * расстояния прежние
   */
  ...(compactTop && {
    [`& .${homepageTitleClasses.root}`]: {
      paddingTop: theme.spacing(2),
    },
    [`& > :not(:empty) ~ * .${homepageTitleClasses.root}`]: {
      paddingTop: theme.spacing(3),
      [theme.breakpoints.up("sm")]: {
        paddingTop: theme.spacing(5),
      },
    },
  }),
}));

const ls = new LocalStorage(`apollo`);

export default function AppShell({ children }) {
  const { t } = useTranslation("arena");
  const viewer = useViewer();
  const arena = useArena();
  const client = useApolloClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { isPlayerFullscreen } = usePlayerFullscreen();
  const isMobileApp = useIsMobileApp();
  const isAppOnlyPage = useIsAppOnlyPage(location.pathname);

  /**
   * В WebView приложения шапку и футер заменяет навигация приложения.
   * Техническая страница говорит об этом сама, а на остальных страницах,
   * куда приложение перешло дальше, платформу помнит cookie
   */
  const isAppMode = isAppOnlyPage || isMobileApp;
  const withoutHeader = isPlayerFullscreen || isAppMode;

  const [searchParams] = useSearchParams();
  const hasSignInParam = searchParams.has("signin");
  const oauthConnect = searchParams.get("oauthConnect");
  const addDevice = searchParams.get("addDevice");
  const hasAddDevice = searchParams.has("addDevice");

  const [mobileOpen, setMobileOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(hasAddDevice || hasSignInParam);
  const [settingsOpen, setSettingsOpen] = useState(
    oauthConnect || hasAddDevice ? true : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hasSignInParam) return;

    if (!signInOpen) {
      if (hasSignInParam) {
        setSignInOpen(true);
      }
    }

    // Очистим signin в адресной строке, сохраняя остальные параметры
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("signin");
    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [hasSignInParam]);

  const [menuItemDialog, setMenuItemDialog] = useState();
  const [galleryMenuItemDialog, setGalleryMenuItemDialog] = useState();

  const [signOut] = useMutation(SignOutMutation, {
    onCompleted() {
      ls.removeItem("token");
      client.resetStore();
    },
  });

  const onCloseMobileDrawer = () => {
    setMobileOpen(false);
    analytics.event({
      category: "Header",
      action: "Close mobile menu",
    });
  };

  const onClickMenuButton = () => {
    setMobileOpen((v) => !v);
    analytics.event({
      category: "Header",
      action: "Click to menu button",
    });
  };

  const onCloseSignIn = () => {
    setSignInOpen(false);
    analytics.event({
      category: "SignInDialog",
      action: "Close dialog",
    });
  };

  const onCloseSettingsDialog = () => {
    setSettingsOpen(false);
    analytics.event({
      category: "SettingsDialog",
      action: "Close dialog",
    });
  };

  const onClickSignIn = () => {
    setSignInOpen(true);
    analytics.event({
      category: "Header",
      action: "Click to sign in button",
    });
  };

  const onClickSearch = () => {
    analytics.event({
      category: "Header",
      action: "Click to search button",
    });
  };

  const onClickMenuItem = (event, menuItem) => {
    if (menuItem.type === "DIALOG") {
      setMenuItemDialog(menuItem);
      analytics.event({
        category: "Header",
        action: "Click to menu item (dialog)",
        label: menuItem.label,
      });
    }

    if (menuItem.type === "GALLERY_DIALOG") {
      setGalleryMenuItemDialog(menuItem);
      analytics.event({
        category: "Header",
        action: "Click to menu item (gallery dialog)",
        label: menuItem.label,
      });
    }

    if (menuItem.type === "LINK") {
      analytics.event({
        category: "Header",
        action: "Click to menu item (link)",
        label: menuItem.label,
      });
    }
  };

  const onClickMobileMenuItem = (event, menuItem) => {
    setMobileOpen(false);
    if (menuItem.type === "DIALOG") {
      setMenuItemDialog(menuItem);
      analytics.event({
        category: "MobileMenu",
        action: "Click to menu item (dialog)",
        label: menuItem.label,
      });
    }

    if (menuItem.type === "GALLERY_DIALOG") {
      setGalleryMenuItemDialog(menuItem);
      analytics.event({
        category: "MobileMenu",
        action: "Click to menu item (gallery dialog)",
        label: menuItem.label,
      });
    }

    if (menuItem.type === "LINK") {
      analytics.event({
        category: "MobileMenu",
        action: "Click to menu item (link)",
        label: menuItem.label,
      });
    }
  };

  const onCloseMenuItemDialog = () => {
    analytics.event({
      category: "MobileMenuItemDialog",
      action: "Close",
      label: menuItemDialog.label,
    });
    setMenuItemDialog();
  };

  const onCloseGalleryMenuItemDialog = () => {
    analytics.event({
      category: "GalleryMenuItemDialog",
      action: "Close",
      label: galleryMenuItemDialog.label,
    });
    setGalleryMenuItemDialog();
  };

  const menuItems = [
    {
      icon: SkuflixSettingsIcon,
      onClick() {
        setSettingsOpen(true);
        analytics.event({
          category: "Header",
          action: "Click to settings in user menu",
        });
      },
      primary: t("userMenu.settings"),
      access: true,
    },
    {
      icon: ShopIcon,
      to: "/purchases",
      onClick() {
        analytics.event({
          category: "Header",
          action: "Click to purchases in user menu",
        });
      },
      primary: t("userMenu.purchases"),
      access: true,
    },
    {
      icon: FavoriteIcon,
      to: "/favorites",
      onClick() {
        analytics.event({
          category: "Header",
          action: "Click to favorites in user menu",
        });
      },
      primary: t("userMenu.favorites"),
      access: arena.isFavoritesEnabled,
    },
    {
      icon: PowerSettingsNewIcon,
      onClick() {
        signOut();
        setSettingsOpen(false);
        analytics.event({
          category: "Header",
          action: "Click to logout",
        });
      },
      primary: t("userMenu.logout"),
      access: !isTelegramMiniApp,
    },
    {
      icon: TuneIcon,
      to: arena.partnerUrl,
      primary: t("userMenu.manage"),
      access: arena.partnerUrl,
      dividerBefore: true,
    },
  ].filter((item) => Boolean(item.access));

  return (
    <AppModeProvider value={isAppMode}>
      <Root>
        {!withoutHeader && (
          <Header
            menuItems={menuItems}
            onClickMenuMobile={onClickMenuButton}
            onClickSignIn={onClickSignIn}
            onClickSearch={onClickSearch}
            onClickMenuItem={onClickMenuItem}
          />
        )}

        <Content withoutHeader={withoutHeader} compactTop={isAppMode}>
          {children}

          {!withoutHeader && <Footer arena={arena} />}
        </Content>

        <AppDrawer
          open={mobileOpen}
          onClose={onCloseMobileDrawer}
          onClickMenuItem={onClickMobileMenuItem}
          viewer={viewer}
          arena={arena}
        />

        {viewer ? (
          <SettingsDialog
            oauthConnect={oauthConnect}
            hasAddDevice={hasAddDevice}
            addDevice={addDevice}
            open={settingsOpen}
            onClose={onCloseSettingsDialog}
          />
        ) : (
          <SignInDialog
            arena={arena}
            open={signInOpen}
            onClose={onCloseSignIn}
            location={location}
          />
        )}

        {viewer && <OnboardingDialog arena={arena} viewer={viewer} />}
        {viewer?.emailError && !isTelegramMiniApp && (
          <EmailErrorDialog viewer={viewer} />
        )}

        <MenuItemDialog
          open={!!menuItemDialog}
          onClose={onCloseMenuItemDialog}
          menuItem={menuItemDialog}
        />

        <GalleryMenuItemDialog
          open={!!galleryMenuItemDialog}
          onClose={onCloseGalleryMenuItemDialog}
          menuItem={galleryMenuItemDialog}
        />
      </Root>
    </AppModeProvider>
  );
}

AppShell.propTypes = {
  children: PropTypes.node,
};
